import React, { useMemo } from "react";
import { motion } from "framer-motion";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "../hooks/useTranslation";
import LazyImage from "./LazyImage";
import { convertToShamsiRelative } from "../utils/dateUtils";
import { renderTextWithNewlines } from "../utils/textUtils";
import { formatNumber } from "../utils/formatNumber";
import VerificationBadge from "./VerificationBadge";
import type { Comment as CommentType } from "../types/post";

/** Normalized comment shape used by feed/post components (id may be number or string). */
export type CommentListItem = Omit<CommentType, "id" | "userId"> & {
  id: string | number;
  userId?: number | string;
  user?: CommentType["user"];
};

export type CommentSectionProps = {
  /** List of comments (will be filtered to valid items with user). */
  comments: CommentListItem[];
  /** When true, show all comments; when false, cap at initialVisibleCount. */
  showAllComments: boolean;
  /** Max comments to show when showAllComments is false. */
  initialVisibleCount?: number;
  /** Current user's id (for delete button visibility). */
  currentUserId?: number;
  /** Whether user is logged in. */
  isAuthenticated: boolean;
  /** Delete mutation state (isPending) and trigger. */
  deleteCommentMutation: {
    isPending: boolean;
    mutateAsync: (commentId: string) => Promise<unknown>;
  };
  /** Id of the comment currently being deleted (for loading state). */
  deleteTargetId: string;
  /** Called when user requests delete of a comment. */
  onDeleteComment: (commentId: string) => void;
  /** Called when user clicks "view more comments". Optional; if not provided, button is hidden. */
  onViewMoreClick?: () => void;
  /** Optional class for the section container. */
  className?: string;
};

const DEFAULT_VISIBLE_COUNT = 3;

/** Valid comment: has id, string content, and user with name or username. */
function isValidComment(c: CommentListItem): c is CommentListItem {
  return Boolean(
    c &&
      c.id != null &&
      typeof c.content === "string" &&
      c.user &&
      (c.user.username ?? c.user.name)
  );
}

function CommentSection({
  comments,
  showAllComments,
  initialVisibleCount = DEFAULT_VISIBLE_COUNT,
  currentUserId,
  isAuthenticated,
  deleteCommentMutation,
  deleteTargetId,
  onDeleteComment,
  onViewMoreClick,
  className = "",
}: CommentSectionProps) {
  const { t } = useTranslation();
  const validComments = useMemo(
    () => (Array.isArray(comments) ? comments.filter(isValidComment) : []),
    [comments]
  );

  const visibleComments = useMemo(
    () =>
      showAllComments
        ? validComments
        : validComments.slice(0, initialVisibleCount),
    [validComments, showAllComments, initialVisibleCount]
  );

  const hasMore = !showAllComments && validComments.length > initialVisibleCount;
  const moreCount = hasMore ? validComments.length - initialVisibleCount : 0;

  if (validComments.length === 0) {
    return null;
  }

  return (
    <div
      className={`px-4 py-2 border-t border-gray-100 dark:border-gray-800 ${className}`.trim()}
      role="region"
      aria-label={t('post.comment')}
    >
      <ul className="list-none p-0 m-0">
        {visibleComments.map((comment) => (
          <CommentItem
            key={String(comment.id)}
            comment={comment}
            isAuthenticated={isAuthenticated}
            currentUserId={currentUserId}
            isDeleting={
              deleteCommentMutation.isPending &&
              deleteTargetId === String(comment.id)
            }
            onDelete={onDeleteComment}
          />
        ))}
      </ul>

      {hasMore && onViewMoreClick && (
        <div className="pt-2 pb-1">
          <button
            type="button"
            onClick={onViewMoreClick}
            className="text-[13px] text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors duration-200"
          >
            مشاهده {formatNumber(moreCount)} کامنت دیگر
          </button>
        </div>
      )}
    </div>
  );
}

type CommentItemProps = {
  comment: CommentListItem;
  isAuthenticated: boolean;
  currentUserId?: number;
  isDeleting: boolean;
  onDelete: (commentId: string) => void;
};

function CommentItem({
  comment,
  isAuthenticated,
  currentUserId,
  isDeleting,
  onDelete,
}: CommentItemProps) {
  const { t } = useTranslation();
  const isOptimistic = String(comment.id).startsWith("temp-");
  const canDelete =
    isAuthenticated &&
    currentUserId != null &&
    comment.userId != null &&
    Number(currentUserId) === Number(comment.userId) &&
    !isDeleting;

  const displayName =
    comment.user?.name ?? comment.user?.username ?? "کاربر";

  return (
    <motion.li
      initial={{ opacity: 1, height: "auto" }}
      animate={{
        opacity: isDeleting ? 0.3 : isOptimistic ? 0.7 : 1,
        height: "auto",
      }}
      exit={{ opacity: 0, height: 0 }}
      className={`flex items-start justify-between py-3 group ${
        isDeleting ? "pointer-events-none" : ""
      }`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <LazyImage
          src={comment.user?.image ?? "/default-avatar.svg"}
          alt={displayName}
          className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-gray-200 dark:ring-gray-700"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/default-avatar.svg";
          }}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="text-[13px] font-bold text-gray-900 dark:text-white">
              {displayName}
            </span>
            <VerificationBadge verified={comment.user?.verified} size="sm" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              {convertToShamsiRelative(comment.createdAt)}
            </span>
            {isOptimistic && (
              <span className="text-xs text-blue-500">{t('common.sending')}</span>
            )}
            {isDeleting && (
              <span className="text-xs text-red-500">در حال حذف...</span>
            )}
          </div>
          <p className="text-[15px] text-gray-900 dark:text-gray-100 leading-relaxed whitespace-pre-wrap break-words">
            {renderTextWithNewlines(comment.content)}
          </p>
        </div>
      </div>

      {canDelete && (
        <motion.button
          type="button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onDelete(String(comment.id))}
          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all text-gray-400 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 me-2 flex-shrink-0"
          title={t('post.deleteComment')}
          aria-label={t('post.deleteComment')}
        >
          <DeleteOutlineIcon className="w-4 h-4" />
        </motion.button>
      )}
    </motion.li>
  );
}

export default CommentSection;
